import { useCallback, useEffect, useRef, useState } from "react";
import { PDFDocument } from 'pdf-lib';

const SCROLL_SPEEDS = { slow: 20, medium: 45, fast: 90 }; // px/s
const SPEED_CYCLE = ['slow', 'medium', 'fast'];
let _sessionScrollSpeed = 'medium';

function KeyHintChip({ label }) {
  return (
    <span className="inline-flex items-center justify-center rounded
                     border border-white/20 bg-white/[0.07]
                     px-2 py-0.5 font-mono text-[12px]
                     text-white/75 leading-snug min-w-[28px]">
      {label}
    </span>
  );
}

export default function LiveModeViewer({
  setlistName,
  songs,
  currentIndex,
  resolvedUrl,
  isLoadingUrl,
  onNavigate,
  onClose,
  isOfflineReady = false,
  showCoachmark = false,
  onCoachmarkDismiss,
}) {
  const song = songs[currentIndex];
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === songs.length - 1;

  const touchStartX = useRef(null);
  const touchStartY = useRef(null);

  const HINT_KEY = "bos_livemode_hint_seen";
  const COACHMARK_KEY = "bos_live_coachmark_seen";

  const coachmarkActive = showCoachmark && (() => {
    try { return !localStorage.getItem(COACHMARK_KEY); } catch { return true; }
  })();

  const [showHint, setShowHint] = useState(() => {
    if (coachmarkActive) return false;
    try {
      return !localStorage.getItem(HINT_KEY);
    } catch {
      return true;
    }
  });

  const [hintFading, setHintFading] = useState(false);
  const [showSwipeHint, setShowSwipeHint] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [scrolling, setScrolling] = useState(false);
  const [scrollSpeed, setScrollSpeed] = useState(_sessionScrollSpeed);
  const [pdfBaseHeight, setPdfBaseHeight] = useState(null);

  const scrollContainerRef = useRef(null);
  const animRef = useRef(null);
  const lastTimeRef = useRef(null);
  const dragStartRef = useRef(null);
  const scrollSpeedRef = useRef(_sessionScrollSpeed);
  const pdfBaseHeightRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (!showSwipeHint) return;
    const t = setTimeout(() => setShowSwipeHint(false), 2000);
    return () => clearTimeout(t);
  }, [showSwipeHint]);

  useEffect(() => {
    setZoomLevel(1);
    setPdfBaseHeight(null);
  }, [currentIndex, resolvedUrl]);

  useEffect(() => {
    if (!resolvedUrl) return;
    let cancelled = false;
    async function measurePdf() {
      try {
        const res = await fetch(resolvedUrl);
        const bytes = await res.arrayBuffer();
        const pdfDoc = await PDFDocument.load(bytes, { ignoreEncryption: true });
        const pages = pdfDoc.getPages();
        if (cancelled) return;
        const containerEl = scrollContainerRef.current;
        const containerWidth = containerEl ? containerEl.clientWidth : window.innerWidth;
        let totalHeight = 0;
        for (const page of pages) {
          const { width, height: pageH } = page.getSize();
          totalHeight += (pageH / width) * containerWidth;
        }
        setPdfBaseHeight(totalHeight);
      } catch {
        // keep null → fall back to 600vh
      }
    }
    measurePdf();
    return () => { cancelled = true; };
  }, [resolvedUrl]);

  useEffect(() => {
    if (!showHint) return;
    const fadeTimer = setTimeout(() => setHintFading(true), 2500);
    const hideTimer = setTimeout(() => {
      setShowHint(false);
      try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
    }, 3000);
    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(hideTimer);
    };
  }, [showHint]);

  useEffect(() => {
    scrollSpeedRef.current = scrollSpeed;
    _sessionScrollSpeed = scrollSpeed;
  }, [scrollSpeed]);

  useEffect(() => {
    pdfBaseHeightRef.current = pdfBaseHeight;
  }, [pdfBaseHeight]);

  useEffect(() => {
    if (!scrolling) {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTimeRef.current = null;
      return;
    }
    function tick(ts) {
      if (lastTimeRef.current === null) lastTimeRef.current = ts;
      const dt = (ts - lastTimeRef.current) / 1000;
      lastTimeRef.current = ts;
      const el = scrollContainerRef.current;
      if (el) {
        const ph = pdfBaseHeightRef.current;
        const maxScroll = ph
          ? Math.max(0, ph - el.clientHeight)
          : Math.min(el.scrollHeight - el.clientHeight, el.clientHeight * 5);
        if (el.scrollTop >= maxScroll - 1) {
          setScrolling(false);
          return;
        }
        el.scrollTop += SCROLL_SPEEDS[scrollSpeedRef.current] * dt;
      }
      animRef.current = requestAnimationFrame(tick);
    }
    animRef.current = requestAnimationFrame(tick);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      lastTimeRef.current = null;
    };
  }, [scrolling]);

  useEffect(() => {
    setScrolling(false);
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [currentIndex]);

  const dismissHint = useCallback(() => {
    if (!showHint) return;
    setHintFading(false);
    setShowHint(false);
    try { localStorage.setItem(HINT_KEY, "1"); } catch { /* ignore */ }
  }, [showHint]);

  const dismissCoachmark = useCallback(() => {
    try {
      localStorage.setItem(COACHMARK_KEY, "1");
      localStorage.setItem(HINT_KEY, "1");
    } catch { /* ignore */ }
    onCoachmarkDismiss?.();
  }, [onCoachmarkDismiss]);

  const handleKeyDown = useCallback(
    (e) => {
      dismissHint();
      if (e.key === ' ') {
        e.preventDefault();
        setScrolling(s => !s);
      } else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        if (!isLast && !isLoadingUrl) onNavigate(currentIndex + 1);
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        if (!isFirst && !isLoadingUrl) onNavigate(currentIndex - 1);
      } else if (e.key === "Escape") {
        onClose();
      }
    },
    [currentIndex, dismissHint, isFirst, isLast, isLoadingUrl, onNavigate, onClose]
  );

  const autoFocusRef = useCallback((el) => {
    if (el) el.focus();
  }, []);

  return (
    <div
      ref={autoFocusRef}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className="fixed inset-0 z-50 flex flex-col bg-black outline-none"
    >
      {/* Top bar */}
      <div className="flex h-12 shrink-0 items-center gap-3 border-b border-border bg-canvas px-3">
        {/* Offline badge */}
        {isOfflineReady && (
          <span className="hidden sm:inline-flex shrink-0 items-center gap-1 rounded-full bg-accent-green/15 px-2 py-0.5 text-[10px] font-medium text-accent-green">
            <span aria-hidden="true">✓</span> Offline
          </span>
        )}
        {/* Setlist name */}
        <span className="hidden text-[11px] uppercase tracking-widest text-white/55 sm:block">
          {setlistName}
        </span>
        <span className="hidden text-white/40 sm:block">·</span>

        {/* Song title + artist */}
        <span className="flex-1 min-w-0 truncate text-[15px] font-semibold tracking-[-0.01em] text-white/95">
          {song.title}
        </span>
        {song.artist && (
          <span className="hidden text-xs text-white/55 sm:block">{song.artist}</span>
        )}

        {/* Counter */}
        <span className="shrink-0 rounded bg-white/[0.08] px-2 py-0.5 text-[13px] font-medium tabular-nums text-white/90">
          {currentIndex + 1} <span className="text-white/40">/</span> {songs.length}
        </span>

        {/* Zoom controls */}
        <button
          type="button"
          aria-label="Zoom out"
          onClick={() => setZoomLevel(z => Math.max(0.5, z - 0.25))}
          disabled={zoomLevel <= 0.5}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border bg-surface text-white/80 disabled:opacity-20"
        >
          −
        </button>
        <span className="shrink-0 text-[13px] tabular-nums text-white/90 w-10 text-center">
          {Math.round(zoomLevel * 100)}%
        </span>
        <button
          type="button"
          aria-label="Zoom in"
          onClick={() => setZoomLevel(z => Math.min(3, z + 0.25))}
          disabled={zoomLevel >= 3}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border bg-surface text-white/80 disabled:opacity-20"
        >
          +
        </button>

        {/* Auto-scroll controls */}
        <button
          type="button"
          aria-label={scrolling ? "Pause auto-scroll" : "Start auto-scroll"}
          onClick={() => setScrolling(s => !s)}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border bg-surface text-white/80"
        >
          {scrolling ? '⏸' : '▶'}
        </button>
        <button
          type="button"
          aria-label="Cycle scroll speed"
          onClick={() => setScrollSpeed(s => SPEED_CYCLE[(SPEED_CYCLE.indexOf(s) + 1) % SPEED_CYCLE.length])}
          className="flex h-10 w-10 sm:h-8 sm:w-6 items-center justify-center rounded-md border border-border bg-surface text-[11px] font-medium text-white/80"
        >
          {scrollSpeed[0].toUpperCase()}
        </button>

        {/* Prev */}
        <button
          type="button"
          aria-label="Previous song"
          onClick={() => onNavigate(currentIndex - 1)}
          disabled={isFirst || isLoadingUrl}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border bg-surface text-xl text-white/80 disabled:opacity-20"
        >
          ‹
        </button>

        {/* Next */}
        <button
          type="button"
          aria-label="Next song"
          onClick={() => onNavigate(currentIndex + 1)}
          disabled={isLast || isLoadingUrl}
          className="flex h-10 w-10 sm:h-8 sm:w-8 items-center justify-center rounded-md border border-border bg-surface text-xl text-white/80 disabled:opacity-20"
        >
          ›
        </button>

        {/* Download */}
        <button
          type="button"
          aria-label="Download PDF"
          data-testid="live-mode-download-btn"
          disabled={!resolvedUrl || isLoadingUrl}
          onClick={() => {
            const a = document.createElement("a");
            a.href = resolvedUrl;
            a.download = `${song.title}.pdf`;
            a.click();
          }}
          className="flex h-10 sm:h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 text-xs text-text-secondary disabled:opacity-20"
        >
          <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 2v8M5 7l3 3 3-3" />
            <path d="M3 13h10" />
          </svg>
          <span className="hidden sm:inline">Download</span>
        </button>

        {/* Exit */}
        <button
          type="button"
          onClick={onClose}
          className="ml-1 flex h-10 sm:h-8 items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 text-xs text-text-secondary"
        >
          <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
            <path d="M3 3l10 10M13 3L3 13" />
          </svg>
          <span className="hidden sm:inline">Exit</span>
        </button>
      </div>

      {/* PDF area */}
      <div
        ref={scrollContainerRef}
        className="relative flex-1 overflow-auto"
        onTouchStart={(e) => {
          touchStartX.current = e.touches[0].clientX;
          touchStartY.current = e.touches[0].clientY;
        }}
        onTouchEnd={(e) => {
          if (touchStartX.current === null) return;
          if (zoomLevel > 1) { touchStartX.current = null; touchStartY.current = null; return; }
          const dx = e.changedTouches[0].clientX - touchStartX.current;
          const dy = e.changedTouches[0].clientY - touchStartY.current;
          if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 48) {
            if (dx < 0 && !isLast && !isLoadingUrl) {
              onNavigate(currentIndex + 1);
            } else if (dx > 0 && !isFirst && !isLoadingUrl) {
              onNavigate(currentIndex - 1);
            }
          }
          touchStartX.current = null;
          touchStartY.current = null;
        }}
      >
        {isLoadingUrl && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 bg-black/60">
            <svg className="h-6 w-6 animate-spin text-white/70" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
            </svg>
            <span className="text-sm text-white/70">Loading…</span>
          </div>
        )}
        {resolvedUrl && (
          <div style={{
            position: 'relative',
            width: `${zoomLevel * 100}%`,
            height: `max(600vh, ${zoomLevel * 100}%)`,
            minWidth: '100%',
            minHeight: '600vh',
          }}>
            <iframe
              key={resolvedUrl}
              src={`${resolvedUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              title={song.title}
              className="border-0"
              style={{ width: '100%', height: '100%', display: 'block', pointerEvents: 'none' }}
            />
            {zoomLevel > 1 && (
              <div
                className="absolute inset-0 hidden sm:block"
                style={{ cursor: 'grab' }}
                onPointerDown={(e) => {
                  const el = scrollContainerRef.current;
                  if (!el) return;
                  e.preventDefault();
                  e.currentTarget.setPointerCapture(e.pointerId);
                  e.currentTarget.style.cursor = 'grabbing';
                  dragStartRef.current = { x: e.clientX, y: e.clientY, scrollLeft: el.scrollLeft, scrollTop: el.scrollTop };
                }}
                onPointerMove={(e) => {
                  if (!dragStartRef.current) return;
                  const el = scrollContainerRef.current;
                  if (!el) return;
                  el.scrollLeft = dragStartRef.current.scrollLeft - (e.clientX - dragStartRef.current.x);
                  el.scrollTop = dragStartRef.current.scrollTop - (e.clientY - dragStartRef.current.y);
                }}
                onPointerUp={(e) => { e.currentTarget.style.cursor = 'grab'; dragStartRef.current = null; }}
                onPointerCancel={(e) => { e.currentTarget.style.cursor = 'grab'; dragStartRef.current = null; }}
              />
            )}
          </div>
        )}
        {!showHint && (
          <div
            className="absolute inset-0 z-10 sm:hidden"
            style={{ touchAction: zoomLevel > 1 ? "pan-x pan-y" : "pan-y" }}
            onTouchStart={(e) => {
              touchStartX.current = e.touches[0].clientX;
              touchStartY.current = e.touches[0].clientY;
            }}
            onTouchEnd={(e) => {
              if (touchStartX.current === null) return;
              if (zoomLevel > 1) { touchStartX.current = null; touchStartY.current = null; return; }
              const dx = e.changedTouches[0].clientX - touchStartX.current;
              const dy = e.changedTouches[0].clientY - touchStartY.current;
              if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 48) {
                if (dx < 0 && !isLast && !isLoadingUrl) {
                  onNavigate(currentIndex + 1);
                } else if (dx > 0 && !isFirst && !isLoadingUrl) {
                  onNavigate(currentIndex - 1);
                }
              }
              touchStartX.current = null;
              touchStartY.current = null;
            }}
          />
        )}
        {showSwipeHint && (
          <div className="pointer-events-none absolute bottom-16 left-0 right-0
                          flex justify-center sm:hidden animate-fade-in">
            <span className="rounded-full bg-black/60 px-4 py-1.5 text-xs
                             text-white/60 backdrop-blur-sm">
              Swipe to navigate
            </span>
          </div>
        )}
        {showHint && (
          <div
            onClick={dismissHint}
            className={[
              "absolute inset-0 z-20 flex items-end justify-center pb-16 sm:pb-12",
              "pointer-events-auto cursor-pointer",
              "transition-opacity duration-500",
              hintFading ? "opacity-0" : "opacity-100",
            ].join(" ")}
            aria-hidden="true"
          >
            <div className="flex flex-col items-center gap-3 rounded-xl
                            bg-black/70 px-6 py-4 backdrop-blur-sm
                            border border-white/[0.08]">

              {/* Keyboard hints — hidden on touch-only devices */}
              <div className="hidden sm:flex items-center gap-6">
                <div className="flex items-center gap-2">
                  <KeyHintChip label="←" />
                  <KeyHintChip label="→" />
                  <span className="text-[12px] text-white/60">navigate songs</span>
                </div>
                <div className="h-3 w-px bg-white/20" />
                <div className="flex items-center gap-2">
                  <KeyHintChip label="Space" />
                  <span className="text-[12px] text-white/60">auto-scroll</span>
                </div>
                <div className="h-3 w-px bg-white/20" />
                <div className="flex items-center gap-2">
                  <KeyHintChip label="Esc" />
                  <span className="text-[12px] text-white/60">exit</span>
                </div>
              </div>

              {/* Touch hint — shown on mobile */}
              <div className="flex items-center gap-2 sm:hidden">
                <span className="text-[13px] text-white/70">Swipe left or right to navigate</span>
              </div>

              <span className="text-[11px] text-white/35">
                Tap anywhere to dismiss
              </span>
            </div>
          </div>
        )}
        {coachmarkActive && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center pointer-events-auto"
            aria-modal="true"
            role="dialog"
            aria-label="Live Mode tips"
          >
            <div className="flex flex-col items-center gap-5 rounded-xl bg-black/80 px-8 py-6 backdrop-blur-sm border border-white/[0.10] max-w-xs w-full mx-4">
              <p className="text-[11px] uppercase tracking-[0.15em] text-white/40 font-medium">Live Mode</p>
              <ul className="w-full space-y-3">
                <li className="flex items-center gap-3">
                  <div className="flex shrink-0 items-center gap-1">
                    <KeyHintChip label="←" />
                    <KeyHintChip label="→" />
                  </div>
                  <span className="text-[13px] text-white/70">Move between songs</span>
                </li>
                <li className="flex items-center gap-3">
                  <span className="shrink-0 text-[13px] text-white/40 w-[52px] text-center">Swipe</span>
                  <span className="text-[13px] text-white/70">Same thing on mobile</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="shrink-0 w-[52px] flex justify-center">
                    <KeyHintChip label="Esc" />
                  </div>
                  <span className="text-[13px] text-white/70">Exit Live Mode</span>
                </li>
              </ul>
              <button
                type="button"
                onClick={dismissCoachmark}
                className="mt-1 w-full rounded-md bg-white/10 border border-white/20 py-2 text-[13px] font-medium text-white/80 hover:bg-white/15 transition-colors"
              >
                Got it
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Bottom song strip */}
      <div className="flex h-12 sm:h-10 shrink-0 items-center gap-1 overflow-x-auto border-t border-border bg-canvas px-3">
        {songs.map((s, idx) => (
          <button
            key={s.id}
            type="button"
            onClick={() => onNavigate(idx)}
            disabled={isLoadingUrl}
            className={[
              "shrink-0 rounded px-2.5 py-1.5 sm:py-1 text-[11px] font-medium",
              idx === currentIndex
                ? "bg-accent text-white"
                : "bg-surface text-text-secondary hover:bg-surface/70",
            ].join(" ")}
          >
            {s.title}
          </button>
        ))}
      </div>
    </div>
  );
}
